import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CAlert, CButton, CCard, CCardBody, CCardHeader, CSpinner, CStepper } from "@coreui/react-pro";
import { membershipsIncluding, useCancelMembership, useMyMemberships } from "../../api/hooks/useMemberships";
import { customerHasActiveSubscription, useMySubscriptions } from "../../api/hooks/useStrategySubscriptions";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { countStrategiesByIds } from "../trading-strategies/strategySides";
import type { ApiError } from "../../api/client";
import { useFailure } from "../../context/useFailure";
import { useToast } from "../../context/useToast";
import { membershipDetailsPath, routePaths } from "../../routes/route-paths";
import { formatDateShort } from "../../lib/dates";

// CStepper's exposed imperative API. Mirrors the strategy End wizard so the two flows stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

export function MembershipEndWizard() {
  const navigate = useNavigate();
  const { tierId = "" } = useParams<{ tierId: string }>();

  const memberships = useMyMemberships();
  const subscriptions = useMySubscriptions();
  const catalogue = useTradingStrategies().data ?? [];
  const endRemote = useCancelMembership();
  const { showSuccess } = useToast();
  const { showFailure, clearFailure } = useFailure();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Only one bought here and still running can be ended here — the same rule the server enforces.
  const membership = memberships.data?.find(
    (m) => m.membershipTierId === tierId && m.boughtHere && m.activeTo === null,
  );

  if (memberships.isLoading || subscriptions.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (memberships.error) {
    return (
      <div>
        <CAlert color="danger">Unable to load your membership. Please try again.</CAlert>
      </div>
    );
  }

  if (!membership) {
    return (
      <div>
        <CAlert color="warning">
          No active membership you can end here.{" "}
          <Link to={routePaths.memberships}>Back to memberships</Link>.
        </CAlert>
      </div>
    );
  }

  const name = `${membership.platformName} · ${membership.tierName}`;
  const detailPath = membershipDetailsPath(tierId);

  // What goes and what stays: a strategy another membership includes, or one the customer pays for, carries on.
  const others = (memberships.data ?? []).filter((m) => m.id !== membership.id);
  // Counted as strategies, not sides. A strategy with one side staying and one going counts in both.
  const stayingIds = membership.tradingStrategyIds.filter(
    (strategyId) =>
      membershipsIncluding(strategyId, others).length > 0 ||
      customerHasActiveSubscription(strategyId, subscriptions.data ?? []),
  );
  const goingIds = membership.tradingStrategyIds.filter((strategyId) => !stayingIds.includes(strategyId));
  const stays = countStrategiesByIds(stayingIds, catalogue);
  const goes = countStrategiesByIds(goingIds, catalogue);
  const includedCount = countStrategiesByIds(membership.tradingStrategyIds, catalogue);

  const handleNext = () => stepperRef.current?.next();

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(detailPath);
    } else {
      stepperRef.current?.prev();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    clearFailure();
    try {
      const ended = await endRemote.mutateAsync(tierId);
      showSuccess(`${name} ends on ${formatDateShort(ended.activeTo)}.`);
      navigate(detailPath);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to end this membership. Please try again.";
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      label: "Details",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Details</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              You're about to end the following membership. Review the details, then continue to confirm.
            </p>
            <dl className="row mb-4">
              <dt className="col-bo-3 text-body-secondary fw-normal">Membership</dt>
              <dd className="col-bo-9 fw-semibold">{name}</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Status</dt>
              <dd className="col-bo-9">Active</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Includes</dt>
              <dd className="col-bo-9">{includedCount} strategies, with their signals</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Billing</dt>
              <dd className="col-bo-9">
                <Link to={routePaths.billing}>See Billing</Link>
              </dd>
            </dl>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleNext}>
                Next: Review
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <CAlert color="warning">
              Your <strong>{name}</strong> membership will end once the period you have already paid for runs out.
            </CAlert>
            <p>When you confirm:</p>
            <ul className="mb-4">
              <li>
                Your membership is marked as <strong>Ending</strong>, and the strategies it includes stay yours to
                the end of the period you have already paid for — not to today.
              </li>
              <li>Billing stops. You will not be charged for it again.</li>
              <li>
                {goes} {goes === 1 ? "strategy goes" : "strategies go"} when it ends
                {stays > 0 && (
                  <>
                    . {stays} {stays === 1 ? "stays" : "stay"}, because{" "}
                    {stays === 1 ? "it's" : "they're"} in another membership or you subscribe to{" "}
                    {stays === 1 ? "it" : "them"}
                  </>
                )}
                .
              </li>
              <li>
                You can resume any time before it ends. After that, you can buy it again from Memberships.
              </li>
            </ul>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack} disabled={submitting}>
                Back
              </CButton>
              <CButton color="danger" onClick={handleSubmit} disabled={submitting}>
                {submitting && <CSpinner size="sm" className="me-2" />}
                End membership
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>End membership</h2>
      <p className="text-body-secondary">
        End your <strong>{name}</strong> membership.
      </p>
      <hr />

      <CStepper ref={stepperRef} steps={steps} onStepChange={setCurrentStep} onReset={() => setCurrentStep(1)} />
    </>
  );
}

export default MembershipEndWizard;
